import { DateTime } from "luxon"
import { z } from "zod"
import { fspConfig } from "../../init/fsp-config"
import { assert } from "../../utils"
import type { TUtcDateTime } from "../types"

// TODO Would be really nice to explicitly set zone and locale here for clarity.
// Even if it's possible to do so earlier. Maybe even stop doing so earlier, because why do it anywhere else?

// -------------------------------------------------------------------------
// ------------------- About zones and locales -----------------------------
// -------------------------------------------------------------------------
// A Luxon DateTime carries a specific millisecond in time, a time zone, and a locale.
// https://moment.github.io/luxon/#/tour?id=your-first-datetime
//
// You can set both explicitly via a config object:
//   const dt = DateTime.local({ zone: "America/New_York", locale: "en-US" })
//   const dt = DateTime.local({ zone: "Atlantic/Reykjavik", locale: "is-IS" })
//   const dt = DateTime.local({ zone: "Europe/Copenhagen", locale: "da-DK" })
//
// Reading them back:
//   dt.zone      — Zone object (includes .zoneName among other things)
//   dt.zoneName  — shorthand for the zone name string
//   dt.locale    — the locale string
//
// Valid zone names: https://nodatime.org/TimeZones
// Valid locale names: https://metacpan.org/pod/DateTime::Locale::Catalog
// Luxon also documents valid zone specifiers (including 'system', 'default', 'utc'):
// https://moment.github.io/luxon/#/zones?id=specifying-a-zone
// Note: 'local' is an alias for 'system'; prefer 'system'.
//
// --- System time zone ---
// When no zone is specified, Luxon uses the OS-configured time zone.
// Check it via DateTime.local().zoneName or Settings.defaultZone.name.
// A VPN changing IP does NOT change the system time zone.
// You can override it without changing the OS setting via Chrome DevTools → Sensors.
// On macOS you can change it with:
//   systemsetup -listtimezones
//   sudo systemsetup -settimezone <timezone>
//
// --- Validation caveat ---
// DateTime.isValid (and .invalidReason) validates zones only — any garbage is accepted as a locale!
// -------------------------------------------------------------------------

// -------------------------------------------------------------------------
// ------------ Arguments for NOT using .now() and .utc() ------------------
// -------------------------------------------------------------------------
// .now() is equivalent to .local() with no arguments — it accepts no config object.
// https://moment.github.io/luxon/#/tour?id=get-the-current-date-and-time
//
// .utc() accepts optional date-part arguments and a config object (locale, etc.), but the zone
// is hardcoded to UTC — effectively .local({ zone: "utc" }) when called with no arguments.
//
// We prefer .local() with an explicit config object over both convenience methods because:
// 1) .now() silently uses Settings.defaultZone and Settings.defaultLocale. Which values those
//    hold depends on what was configured earlier — not obvious from looking at the call site.
// 2) .utc() locks the zone to UTC, so we can't dynamically set it to the fsp's zone.
// -------------------------------------------------------------------------

// Note, if you infer a type from this schema the type will be a plain 'string'.
const dtSchema = z.iso
  .datetime()
  .nullable()
  .refine((val) => val === null || val.endsWith("Z"), {
    error: "Datetime string must end with 'Z' to indicate UTC timezone",
  })

/**
 * A class for all our date and time needs.
 * We try to minimize using Luxon directly in our components, but rather use this class.
 * The reason for this is that we want to be able to mock the time for the app.
 * This is useful for testing and debugging.
 *
 * The appDtStr parameter is a datetime string that ends with Z to indicate that we intend it to be
 * interpreted as UTC. This is because a programmer working on tiffin.dk might be in Iceland or in
 * Denmark, so using the operating system time zone for the appDtStr string is ambiguous.
 * However, always when the date or time is _read_ we want it to take into account the correct zone
 * for the fsp.
 * For example for Tiffin:
 * const dtu = DateTimeUtils.fromObject({ appTime: "2024-09-10T12:37:00Z", zone: "Europe/Copenhagen" })
 * const appDt = dtu.local()
 * appDt.hour
 * 👆 This should return, for the hour, 14 (or 13, depending on daylight savings), not 12, because even though
 * the appDtStr string is UTC the Tiffin zone is Europe/Copenhagen.
 */
export class DateTimeUtils {
  appDtStr: TUtcDateTime | null
  zone: string

  constructor(appDtStr: TUtcDateTime | null) {
    const func = "DateTimeUtils.constructor"

    dtSchema.parse(appDtStr)
    const zone = fspConfig.timeZone

    if (appDtStr !== null) {
      console.log("🕑❗ Mocking app UTC time", { func, appDtStr })
      console.log("🕑 The fsp configured time zone", { func, zone })
    }

    this.appDtStr = appDtStr
    this.zone = zone
  }

  now(): never {
    throw new Error(
      "We never want to use this method because which zone and locale it uses is ambiguous.",
    )
  }

  utc(): never {
    throw new Error(
      "We never want to use this method because which zone and locale it uses is ambiguous.",
    )
  }

  /**
   * Return the current app datetime in the fsp's configured time zone.
   *
   * If appDtStr is null, this returns the real current time in this.zone.
   * If appDtStr is set, it is treated as a UTC instant and then converted to this.zone.
   *
   * This method exists so the app can mock "now" without depending on the browser or operating
   * system time zone.
   *
   * Note that you cannot pass any arguments to this method, as opposed to Luxon's .local() method.
   * See {@link https://moment.github.io/luxon/api-docs/index.html#datetimelocal | Luxon DateTime.local docs}.
   * If you need to construct a specific datetime for some other purpose, use Luxon directly and
   * pass an explicit zone.
   *
   * Example:
   * - Browser/default zone: Asia/Jakarta
   * - FSP zone: Europe/Copenhagen
   * - appDtStr: "2024-09-10T12:37:00Z"
   *
   * Plain Luxon parsing respects the trailing Z and expresses the same instant in the current or
   * default zone:
   * DateTime.fromISO("2024-09-10T12:37:00Z").toISO()
   * // => "2024-09-10T19:37:00.000+07:00"
   *
   * This method instead returns that same instant in the FSP's configured zone:
   * dtu.local().toISO()
   * // => "2024-09-10T14:37:00.000+02:00"
   *
   * @returns A DateTime object that represents the current app datetime in the fsp's configured zone.
   */
  local(): DateTime<true> {
    const func = "DateTimeUtils.local"

    if (this.appDtStr === null) {
      const dt = DateTime.local({ zone: this.zone })
      assert(dt.isValid, `The datetime is invalid: ${dt.invalidReason}`)
      return dt
    }

    // DateTime.fromISO(this.appDtStr) would respect the trailing Z, but unless we pass a zone
    // explicitly the returned DateTime is expressed in Luxon's current/default zone.
    // Example, if the default zone is Asia/Jakarta:
    // DateTime.fromISO("2024-09-10T12:37:00Z").toISO()
    // => "2024-09-10T19:37:00.000+07:00"
    // That is the same instant, but in Jakarta time, which is not what we want here.

    // The forced app time search param is defined to be a UTC instant, so parse it as UTC first.
    const dtUtc = DateTime.fromISO(this.appDtStr, { zone: "UTC" })
    // Then convert that instant into the fsp's configured zone, since app time should be read in
    // restaurant time rather than browser/system time.
    const dt = dtUtc.setZone(this.zone)

    assert(dt.isValid, `The datetime is invalid: ${dt.invalidReason}`)
    return dt
  }

  fromISO(text: string): DateTime<true> {
    // Set the fsp's configured zone.
    const dt = DateTime.fromISO(text, { zone: this.zone })
    assert(dt.isValid, `The datetime is invalid: ${dt.invalidReason}`)
    return dt
  }
}
